/**
 * Request processing capability.
 *
 * Modifies request processing context in a certain way when delegates to handler.
 *
 * Request processing capabilities could be {@link RequestCapability.combine combined}.
 *
 * @typeParam TInput - A type of request processing means required in order to apply this capability.
 * @typeParam TExt - A type of extension to request processing means this capability applies.
 */
export class RequestCapability {
    /**
     * Builds request capability by the given `provider`.
     *
     * @typeParam TInput - A type of request processing means required by this provider.
     * @typeParam TExt - A type of extension to request processing means this provider applies.
     * @param provider - Request processing capability provider.
     *
     * @returns Request processing capability that call the given `provider` in order to apply.
     */
    static of(provider) {
        const capability = {
            for: provider,
            and(next) {
                return RequestCapability.combine(capability, next);
            },
        };
        return capability;
    }
    /**
     * Combines two request processing capabilities.
     *
     * @typeParam TInput - A type of request processing means expected by the `first` capability.
     * @typeParam TExt - A type of request processing means extension applied by the `first` capability.
     * @typeParam TNext - A type of request processing means extension applied by the `second` capability.
     * @param first - First capability to combine.
     * @param second - Second capability to combine. Receives requests modified by the `first` one.
     *
     * @return Combined request processing capability that applies modifications to request by the `first` capability,
     * and then - by the `second` one.
     */
    static combine(first, second) {
        const chain = {
            for(delegate) {
                return first.for(second.for(delegate));
            },
            and(next) {
                return RequestCapability.combine(chain, next);
            },
        };
        return chain;
    }
    /**
     * Combines this capability with the `next` one.
     *
     * @typeParam TNext - A type of extension to request processing means applied by `next` capability.
     * @param next - Next capability that receives requests modified by this capability.
     *
     * @return New request processing capability that applies modifications to request by this capability first,
     * and then - by the `next` one.
     *
     * @see RequestCapability.combine
     */
    and(next) {
        return RequestCapability.combine(this, next);
    }
}
//# sourceMappingURL=request-capability.js.map